import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Normalize string for search - removes diacritics and converts to lowercase.
 * Example: "Košice" → "kosice", "Životné" → "zivotne"
 */
export function normalizeSearch(str: string): string {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();
}

/**
 * Check if current user has a specific capability
 */
export function userCan(capability: string): boolean {
	return window.dddData?.currentUser?.capabilities?.includes(capability) || false;
}

/**
 * Get current datetime in format required by datetime-local input
 * Format: "YYYY-MM-DDTHH:MM"
 */
export function getCurrentDateTime(): string {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Add minutes to a datetime string
 * @param datetime - datetime string in format "YYYY-MM-DDTHH:MM"
 * @param minutes - number of minutes to add
 * @returns datetime string in format "YYYY-MM-DDTHH:MM"
 */
export function addMinutes(datetime: string, minutes: number): string {
	const date = new Date(datetime);
	date.setMinutes(date.getMinutes() + minutes);

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const mins = String(date.getMinutes()).padStart(2, '0');

	return `${year}-${month}-${day}T${hours}:${mins}`;
}

/**
 * Format date string for display
 * @param dateString - ISO date string or datetime-local format
 * @returns formatted date string (e.g., "22. 11. 2024 09:30")
 */
export function formatDate(dateString: string): string {
	if (!dateString) return '—';

	const date = new Date(dateString);
	if (isNaN(date.getTime())) return dateString;

	const day = date.getDate();
	const month = date.getMonth() + 1;
	const year = date.getFullYear();
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');

	return `${day}. ${month}. ${year} ${hours}:${minutes}`;
}

/**
 * Format work address from parts
 * @returns object with name and formatted address
 */
export function formatWorkAddress(
	name?: string | null,
	street?: string | null,
	zip?: string | null,
	city?: string | null
): { name: string; address: string } {
	const addressParts = [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean);
	return {
		name: name || '',
		address: addressParts.join(', '),
	};
}
